import { Head } from "@inertiajs/react"
import { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import { CsvTable } from "@/components/csv/CsvTable"

interface CsvViewerProps {
  filePath: string
  rootFolder: string
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]')
  return meta?.getAttribute("content") || ""
}

function encodeDropboxPath(path: string): string {
  return path
    .split("/")
    .map((s) => encodeURIComponent(s))
    .join("/")
    .replace(/^\//, "")
}

export default function CsvViewer({ filePath }: CsvViewerProps) {
  const [rawContent, setRawContent] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    loadFile(filePath)
  }, [filePath])

  async function loadFile(path: string) {
    setLoading(true)
    setLoadError(false)
    try {
      const res = await fetch(`/api/files/${encodeDropboxPath(path)}`, {
        headers: { "X-CSRF-Token": getCSRFToken() },
      })
      if (!res.ok) throw new Error("Failed to load file")
      const data = await res.json()
      setRawContent(data.raw_content || "")
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }

  const fileName = filePath.split("/").pop() || "CSV File"

  if (loading) {
    return (
      <>
        <Head title={fileName} />
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    )
  }

  if (loadError) {
    return (
      <>
        <Head title={fileName} />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <p className="text-sm text-muted-foreground mb-2">Failed to load file</p>
            <button
              onClick={() => loadFile(filePath)}
              className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
              Try again
            </button>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      <Head title={fileName} />
      <CsvTable filePath={filePath} rawContent={rawContent || ""} />
    </>
  )
}
